import { useState, useCallback } from 'react';
import { Idol, Position, GridState } from 'types';
import { canPlaceItem, updateGridWithPlacedItem } from 'utils';

interface UseDragAndDropResult {
  draggedItem: Idol | null;
  hoverPosition: Position | null;
  handleDragStart: (item: Idol) => void;
  handleDragOver: (row: number, col: number) => void;
  handleDragEnter: (row: number, col: number) => void;
  handleDragLeave: () => void;
  handleDrop: (row: number, col: number, grid: GridState) => {
    success: boolean;
    newGrid?: GridState;
    newItem?: Idol;
  };
  resetDrag: () => void;
}

const useDragAndDrop = (): UseDragAndDropResult => {
  const [draggedItem, setDraggedItem] = useState<Idol | null>(null);
  const [hoverPosition, setHoverPosition] = useState<Position | null>(null);

  const handleDragStart = useCallback((item: Idol) => {
    setDraggedItem(item);
  }, []);

  const handleDragOver = useCallback((row: number, col: number) => {
    if (draggedItem) {
      setHoverPosition({ row, col });
    }
  }, [draggedItem]);

  const handleDragEnter = useCallback((row: number, col: number) => {
    if (draggedItem) {
      setHoverPosition({ row, col });
    }
  }, [draggedItem]);

  const handleDragLeave = useCallback(() => {
    setHoverPosition(null);
  }, []);

  const handleDrop = useCallback((row: number, col: number, grid: GridState) => {
    if (!draggedItem) {
      return { success: false };
    }

    if (canPlaceItem(grid, row, col, draggedItem)) {
      // Create a copy of the dragged item with a new ID and position
      const newItem: Idol = {
        ...draggedItem,
        id: `${draggedItem.id}-${Date.now()}`,
        position: { row, col }
      };
      
      // Update grid cells to mark them as occupied
      const newGrid = updateGridWithPlacedItem(grid, newItem, { row, col });
      
      return {
        success: true,
        newGrid,
        newItem
      };
    }
    
    return { success: false };
  }, [draggedItem]);

  const resetDrag = useCallback(() => {
    setDraggedItem(null);
    setHoverPosition(null);
  }, []);

  return {
    draggedItem,
    hoverPosition,
    handleDragStart,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    resetDrag
  };
};

export default useDragAndDrop;